<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import RadarChart from 'shared/components/charts/RadarChart.vue';
import { clampScore } from './buzzScoreDistribution';
import {
  BUZZ_SIGNAL_ORDER,
  buzzSignalChartColor,
  buzzSignalChartColors,
} from './buzzSignalColors';

const props = defineProps({
  summary: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const signalKeys = {
  reviews: 'CATEGORY_REVIEWS',
  media: 'CATEGORY_MEDIA',
  footfall: 'CATEGORY_FOOTFALL',
};

const signals = computed(() =>
  BUZZ_SIGNAL_ORDER.map(key => ({
    key,
    label: t(`BUZZ_REPORTS.${signalKeys[key]}`),
    value: clampScore(props.summary?.[`b_${key}`]),
  }))
);

const hasSignals = computed(() =>
  signals.value.some(signal => signal.value != null)
);

const chartColors = computed(() => buzzSignalChartColors());

const collection = computed(() => ({
  labels: signals.value.map(signal => signal.label),
  datasets: [
    {
      label: t('BUZZ_REPORTS.SIGNALS.DATASET'),
      data: signals.value.map(signal => signal.value ?? 0),
      borderColor: 'rgba(139, 148, 158, 0.45)',
      backgroundColor: 'rgba(139, 148, 158, 0.08)',
      borderWidth: 2,
      pointBackgroundColor: chartColors.value,
      pointBorderColor: '#FFFFFF',
      pointBorderWidth: 1.5,
      pointRadius: 3,
      pointHoverRadius: 4,
      fill: true,
    },
  ],
}));

const chartOptions = computed(() => ({
  scales: {
    r: {
      ticks: {
        display: false,
      },
      pointLabels: {
        font: { size: 10 },
        color: context =>
          buzzSignalChartColor(BUZZ_SIGNAL_ORDER[context.index]),
      },
    },
  },
}));
</script>

<template>
  <div
    class="w-full h-full flex flex-col m-0.5 rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 overflow-hidden"
  >
    <div class="px-4 pt-2.5 pb-1 shrink-0">
      <p class="text-xs font-medium text-n-slate-12 mb-0">
        {{ t('BUZZ_REPORTS.SIGNALS.TITLE') }}
      </p>
    </div>
    <div v-if="!hasSignals" class="px-4 pb-3 text-xs text-n-slate-10">
      {{ t('BUZZ_REPORTS.SIGNALS.NO_DATA') }}
    </div>
    <div v-else class="relative flex-1 min-h-0 px-2 pb-2">
      <div class="absolute inset-2 bottom-2">
        <RadarChart :collection="collection" :chart-options="chartOptions" />
      </div>
    </div>
  </div>
</template>
